import React, { useMemo } from 'react'
import { StyleSheet, View } from 'react-native'

import { Avatar } from 'widgets/Avatar'
import { Box } from 'widgets/Box'
import { Icon } from 'widgets/Icon'
import { Text } from 'widgets/Text'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useUser } from 'hooks/useUser'

import { links } from 'utils/constants'

import { useFocusMode } from 'contexts/FocusModeContext'
import { useTheme } from 'contexts/ThemeContext'

import { StyledPressable } from './styled'

const iconNames: any = {
  [links.HOME_STACK]: 'home',
  [links.NOTIFICATIONS_STACK]: 'notifications',
  [links.CHAT_STACK]: 'messages',
}

type CustomTabBarProps = {
  descriptors: any
  navigation: any
  state: any
  route: any
  index: number
  notifierCount?: number
}

export const CustomTabBarIcon = ({
  descriptors,
  navigation,
  state,
  route,
  index,
  notifierCount = 0,
}: CustomTabBarProps) => {
  const { data: currentUser } = useCurrentUser()
  const theme = useTheme()
  const focusMode = useFocusMode()
  const isFocusMode = useMemo(() => focusMode.group !== undefined, [focusMode.group])
  const { data: user } = useUser({ userId: focusMode.group?.trainer.id })

  const { options } = descriptors[route.key]
  let label = route.name
  if (options.tabBarLabel) {
    label = options.tabBarLabel
  } else if (options.title) {
    label = options.title
  }

  const isFocused = state.index === index

  const onPress = () => {
    const event = navigation.emit({
      type: 'tabPress',
      target: route.key,
      canPreventDefault: true,
    })
    if (!isFocused && !event.defaultPrevented) {
      // navigation.navigate({ name: route.name, merge: true })
      // Note - this works well for web, but is untested for native
      navigation.reset({ index: 0, routes: [{ name: route.name }] })
    } else if (isFocused && !event.defaultPrevented) {
      navigation.reset({ index: 0, routes: [{ name: route.name }] })
    }
  }

  const onLongPress = () => {
    navigation.emit({
      type: 'tabLongPress',
      target: route.key,
    })
  }

  const computedStyles = StyleSheet.create({
    text: {
      marginTop: 1,
    },
    profileUnselected: {
      padding: 4,
      top: 4,
      position: 'relative',
    },
    profileSelected: {
      borderColor: theme.colors.v2primary,
      borderWidth: 2,
      borderRadius: theme.spacing.lg,
      padding: 2,
      top: 4,
      position: 'relative',
    },
    unreadIndicatorContainer: {
      position: 'absolute',
      zIndex: 99,
      top: -15,
      right: -15,
    },
    unreadIndicator: {
      top: 2,
    },
  })

  return (
    <StyledPressable
      key={label}
      accessibilityRole='button'
      accessibilityState={isFocused ? { selected: true } : {}}
      accessibilityLabel={options.tabBarAccessibilityLabel}
      testID={options.tabBarTestID}
      onPress={onPress}
      onLongPress={onLongPress}
      // eslint-disable-next-line react-native/no-inline-styles
      style={{ alignItems: 'center' }}
    >
      <Box position='relative' margin={{ top: 2 }}>
        <Box margin='sm' align='center'>
          {route.name !== links.PROFILE_STACK && route.name !== links.GROUP_STACK ? (
            <>
              {notifierCount > 0 && (
                <Box
                  testID='UnreadIndicatorContainer'
                  backgroundColor='white'
                  borderRadius='md'
                  align='center'
                  width={26}
                  height={26}
                  style={computedStyles.unreadIndicatorContainer}
                >
                  <Box
                    testID='UnreadIndicator'
                    backgroundColor='v2secondary'
                    borderRadius='md'
                    align='center'
                    width={22}
                    height={22}
                    style={computedStyles.unreadIndicator}
                  >
                    <Text fontSize='sm' color='white' align='center' style={computedStyles.text}>
                      {notifierCount}
                    </Text>
                  </Box>
                </Box>
              )}
              <Icon
                name={`${iconNames[route.name]}${isFocused ? '-selected' : '-unselected'}`}
                size={34}
                color={isFocusMode ? 'white' : 'v2black'}
              />
            </>
          ) : (
            <View
              style={isFocused ? computedStyles.profileSelected : computedStyles.profileUnselected}
            >
              <Avatar width={32} height={32} image={user?.avatar ?? currentUser?.avatar} />
            </View>
          )}
        </Box>
      </Box>
    </StyledPressable>
  )
}
